/**
 * Doserne — Sesizări, Plângeri & Sugestii ("Reports").
 *
 * Every Doserne member can post a report; admins see everything and
 * can set status + reply. Anonymous mode is enforced server-side.
 *
 * UI layout:
 *  - Big empathetic hero header explaining the purpose (mirrors the
 *    exact copy the user provided so tone stays consistent).
 *  - Compose form: category chips, subject, body, anonymous toggle.
 *  - Below: worker sees their own signed reports; admin sees an all-
 *    reports table with filters + inline actions.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Send, Shield, User as UserIcon, EyeOff, MessageSquare, CheckCircle2,
  AlertCircle, Sparkles, Users, Building2, ClipboardList, Lightbulb, HelpCircle,
  Filter, Trash2, ChevronDown, ChevronUp,
} from "lucide-react";
import { useDoserneAuth } from "./doserneAuth";
import { useLang } from "./i18n";
import DoserneShell from "./DoserneShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { id: "colleague",    labels: { ro: "Coleg / Echipă",  no: "Kollega / Team",  en: "Colleague / Team" },       icon: Users },
  { id: "company",      labels: { ro: "Companie",        no: "Firma",           en: "Company" },                icon: Building2 },
  { id: "organization", labels: { ro: "Organizare",      no: "Organisering",    en: "Organization" },           icon: ClipboardList },
  { id: "management",   labels: { ro: "Management",      no: "Ledelse",         en: "Management" },             icon: Shield },
  { id: "suggestion",   labels: { ro: "Sugestie",        no: "Forslag",         en: "Suggestion" },             icon: Lightbulb },
  { id: "other",        labels: { ro: "Altele",          no: "Annet",           en: "Other" },                  icon: HelpCircle },
];

const STATUS_STYLE = {
  open:      { labels: { ro: "Deschis",    no: "Åpen",            en: "Open" },        bg: "bg-amber-100",   text: "text-amber-700",   icon: AlertCircle },
  in_review: { labels: { ro: "În analiză", no: "Under vurdering", en: "In review" },   bg: "bg-sky-100",     text: "text-sky-700",     icon: Loader2 },
  resolved:  { labels: { ro: "Rezolvat",   no: "Løst",            en: "Resolved" },    bg: "bg-emerald-100", text: "text-emerald-700", icon: CheckCircle2 },
};

function fmtDate(iso, lang) {
  if (!iso) return "";
  try {
    const locale = lang === "ro" ? "ro-RO" : lang === "en" ? "en-GB" : "no-NO";
    return new Date(iso).toLocaleString(locale, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

export default function DoserneReports() {
  const { user, loading: authLoading, authHeader } = useDoserneAuth();
  const { lang } = useLang();
  const [category, setCategory] = useState("suggestion");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [busy, setBusy] = useState(false);
  const [mine, setMine] = useState([]);
  const [allReports, setAllReports] = useState([]);
  const [filterStatus, setFilterStatus] = useState("");
  const [expandedId, setExpandedId] = useState(null);

  const isAdmin = !!(user?.is_admin || user?.role === "platform_admin" || user?.is_doserne_admin);
  const t = useMemo(() => {
    const DICT = {
      ro: {
        hero_title: "Sesizări, Plângeri & Sugestii",
        hero_body:
          "Puteți raporta orice problemă sau situație care vă deranjează: probleme cu un coleg sau în echipă, lucruri care nu se fac corect, probleme de organizare, situații pe care managementul ar trebui să le știe, sau sugestii de îmbunătățire. Puteți trimite sesizarea cu numele vostru sau anonim. Scopul nu este să creăm probleme, ci exact invers: vrem să știm când ceva nu funcționează bine și să rezolvăm împreună. 👍",
        category: "Categorie",
        subject: "Subiect",
        subject_ph: "Ex: «Nu primim materiale la timp pe Klofta»",
        body: "Descrie situația",
        body_ph: "Fii cât de detaliat vrei. Nimeni nu te judecă.",
        anon_label: "Trimite anonim",
        anon_hint: "Managementul va vedea sesizarea, dar NU va ști cine a trimis-o. Excepție: în cazuri grave (amenințări, hărțuire), adminul poate ridica anonimatul — orice acces este înregistrat.",
        submit: "Trimite sesizarea",
        submit_busy: "Se trimite…",
        success: "Mulțumim! Sesizarea a fost înregistrată.",
        fail: "Nu am putut trimite. Încearcă din nou.",
        validation_min: "Subiect ≥3 caractere, descriere ≥5",
        mine_title: "Sesizările mele (cu nume)",
        mine_empty: "Nu ai trimis nicio sesizare cu numele tău. Cele anonime nu apar aici — asta e prețul anonimatului.",
        admin_title: "Toate sesizările (admin)",
        admin_empty: "Nicio sesizare încă.",
        filter_all: "Toate",
        reply_ph: "Scrie un răspuns pentru autor…",
        reply_send: "Trimite răspuns",
        set_status: "Setează status",
        delete_confirm: "Ștergi definitiv această sesizare?",
        deleted: "Șters",
        replied_by: "Răspuns de",
        anonymous_by: "Anonim",
      },
      no: {
        hero_title: "Rapporter, klager & forslag",
        hero_body:
          "Rapportér alt som ikke fungerer bra: problemer med en kollega eller i teamet, ting som ikke gjøres riktig, organiseringsproblemer, situasjoner ledelsen bør vite om, eller forslag til forbedring. Du kan sende med navn eller anonymt. Målet er ikke å skape problemer, men å fikse dem sammen. 👍",
        category: "Kategori",
        subject: "Emne",
        subject_ph: "Ex: «Materialene kommer sent på Klofta»",
        body: "Beskriv situasjonen",
        body_ph: "Vær så detaljert du vil. Ingen dømmer deg.",
        anon_label: "Send anonymt",
        anon_hint: "Ledelsen ser rapporten, men får IKKE vite hvem som sendte den. Unntak: ved alvorlige tilfeller (trusler, trakassering) kan admin bryte anonymiteten — hver tilgang loggføres.",
        submit: "Send rapport",
        submit_busy: "Sender…",
        success: "Takk! Rapporten er registrert.",
        fail: "Kunne ikke sende. Prøv igjen.",
        validation_min: "Emne ≥3 tegn, beskrivelse ≥5",
        mine_title: "Mine rapporter (med navn)",
        mine_empty: "Du har ikke sendt noen med navn ennå. Anonyme rapporter vises ikke her — det er prisen for anonymitet.",
        admin_title: "Alle rapporter (admin)",
        admin_empty: "Ingen rapporter enda.",
        filter_all: "Alle",
        reply_ph: "Skriv et svar til forfatteren…",
        reply_send: "Send svar",
        set_status: "Sett status",
        delete_confirm: "Slette denne rapporten permanent?",
        deleted: "Slettet",
        replied_by: "Svart av",
        anonymous_by: "Anonym",
      },
      en: {
        hero_title: "Reports, complaints & suggestions",
        hero_body:
          "Report anything that bothers you: issues with a colleague or team, things you feel are not done right, organizational or communication problems, situations management should know about, or suggestions for improvement. You can send with your name or anonymously. The point is not to create problems, but exactly the opposite: we want to know when something is off and fix it together. 👍",
        category: "Category",
        subject: "Subject",
        subject_ph: "e.g. \"Materials arrive late at Klofta\"",
        body: "Describe the situation",
        body_ph: "Be as detailed as you want. No one is judging you.",
        anon_label: "Send anonymously",
        anon_hint: "Management will see the report but will NOT know who sent it. Exception: in serious cases (threats, harassment), admin may lift the anonymity — every access is audited.",
        submit: "Send report",
        submit_busy: "Sending…",
        success: "Thanks! Your report has been recorded.",
        fail: "Could not send. Please try again.",
        validation_min: "Subject ≥3 characters, description ≥5",
        mine_title: "My reports (signed)",
        mine_empty: "You haven't sent any signed reports yet. Anonymous ones don't show up here — that's the price of anonymity.",
        admin_title: "All reports (admin)",
        admin_empty: "No reports yet.",
        filter_all: "All",
        reply_ph: "Write a reply to the author…",
        reply_send: "Send reply",
        set_status: "Set status",
        delete_confirm: "Permanently delete this report?",
        deleted: "Deleted",
        replied_by: "Replied by",
        anonymous_by: "Anonymous",
      },
    };
    return DICT[lang] || DICT.no;
  }, [lang]);

  useEffect(() => { if (user) { loadMine(); loadAll(); } }, [user, isAdmin, filterStatus]);

  const loadMine = async () => {
    try {
      const { data } = await axios.get(`${API}/doserne/reports/mine`, authHeader());
      setMine(data.reports || []);
    } catch { /* silent */ }
  };
  const loadAll = async () => {
    if (!isAdmin) return;
    try {
      const url = filterStatus ? `${API}/doserne/reports?status=${filterStatus}` : `${API}/doserne/reports`;
      const { data } = await axios.get(url, authHeader());
      setAllReports(data.reports || []);
    } catch { /* silent */ }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (subject.trim().length < 3 || body.trim().length < 5) {
      toast.error(t.validation_min);
      return;
    }
    setBusy(true);
    try {
      await axios.post(
        `${API}/doserne/reports`,
        { category, subject: subject.trim(), body: body.trim(), is_anonymous: isAnonymous },
        authHeader(),
      );
      toast.success(t.success);
      setSubject("");
      setBody("");
      setIsAnonymous(false);
      loadMine();
      loadAll();
    } catch {
      toast.error(t.fail);
    }
    setBusy(false);
  };

  const patchStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/doserne/reports/${id}/status`, { status }, authHeader());
      loadAll();
    } catch { toast.error(t.fail); }
  };

  const sendReply = async (id, text) => {
    if (!text.trim()) return;
    try {
      await axios.post(`${API}/doserne/reports/${id}/reply`, { reply: text.trim() }, authHeader());
      toast.success(t.success);
      loadAll();
    } catch { toast.error(t.fail); }
  };

  const del = async (id) => {
    if (!window.confirm(t.delete_confirm)) return;
    try {
      await axios.delete(`${API}/doserne/reports/${id}`, authHeader());
      toast.success(t.deleted);
      loadAll();
    } catch { toast.error(t.fail); }
  };

  if (authLoading) return <div className="min-h-screen flex items-center justify-center bg-[#fafaf7]"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
  if (!user) return <Navigate to="/doserne/logg-inn" replace />;

  return (
    <DoserneShell title={t.hero_title}>
      {/* Empathetic explainer */}
      <div className="bg-gradient-to-br from-emerald-50 via-white to-sky-50 border border-emerald-100 rounded-2xl p-4 sm:p-5 mb-5" data-testid="reports-hero">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-500 flex items-center justify-center flex-shrink-0" style={{ color: "#fff" }}>
            <Sparkles className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <h1 className="text-lg font-black text-slate-900 mb-1">{t.hero_title}</h1>
            <p className="text-sm text-slate-700 leading-relaxed">{t.hero_body}</p>
          </div>
        </div>
      </div>

      {/* Compose form */}
      <form onSubmit={submit} className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5 mb-6" data-testid="reports-form">
        <label className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">{t.category}</label>
        <div className="mt-2 flex flex-wrap gap-2">
          {CATEGORIES.map((c) => {
            const Icon = c.icon;
            const active = category === c.id;
            const label = c.labels[lang] || c.labels.no;
            return (
              <button
                type="button"
                key={c.id}
                onClick={() => setCategory(c.id)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-black transition ${active ? "bg-slate-900 border-slate-900 shadow" : "bg-white border border-slate-300 text-slate-700 hover:border-slate-500"}`}
                style={active ? { color: "#fff" } : {}}
                data-testid={`reports-cat-${c.id}`}
              >
                <Icon className="w-3.5 h-3.5" /> {label}
              </button>
            );
          })}
        </div>

        <div className="mt-4">
          <label className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">{t.subject}</label>
          <input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder={t.subject_ph}
            maxLength={200}
            className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg text-sm"
            data-testid="reports-subject"
          />
        </div>

        <div className="mt-3">
          <label className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">{t.body}</label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={t.body_ph}
            rows={5}
            maxLength={5000}
            className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg text-sm"
            data-testid="reports-body"
          />
        </div>

        <label className="mt-3 flex items-start gap-3 p-3 rounded-lg border border-slate-200 hover:border-slate-400 cursor-pointer" data-testid="reports-anon-toggle">
          <input
            type="checkbox"
            checked={isAnonymous}
            onChange={(e) => setIsAnonymous(e.target.checked)}
            className="mt-0.5 w-4 h-4"
          />
          <div className="flex-1">
            <div className="text-sm font-black text-slate-900 inline-flex items-center gap-1.5">
              <EyeOff className="w-4 h-4" /> {t.anon_label}
            </div>
            <div className="text-[11px] text-slate-500">{t.anon_hint}</div>
          </div>
        </label>

        <button
          type="submit"
          disabled={busy}
          className="mt-4 w-full py-3 bg-slate-900 hover:bg-slate-800 disabled:bg-slate-400 rounded-lg text-sm font-black inline-flex items-center justify-center gap-2 shadow"
          style={{ color: "#fff" }}
          data-testid="reports-submit"
        >
          {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> {t.submit_busy}</> : <><Send className="w-4 h-4" /> {t.submit}</>}
        </button>
      </form>

      {/* My reports (signed only) */}
      <section className="mb-6" data-testid="reports-mine-section">
        <h2 className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-2 inline-flex items-center gap-1.5">
          <UserIcon className="w-3.5 h-3.5" /> {t.mine_title}
        </h2>
        {mine.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-xl p-4 text-sm text-slate-500">{t.mine_empty}</div>
        ) : (
          <ul className="space-y-2">
            {mine.map((r) => <ReportCard key={r.id} r={r} lang={lang} t={t} readonly />)}
          </ul>
        )}
      </section>

      {/* Admin all-reports view */}
      {isAdmin && (
        <section data-testid="reports-admin-section">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-xs uppercase tracking-widest text-slate-500 font-bold inline-flex items-center gap-1.5">
              <Shield className="w-3.5 h-3.5" /> {t.admin_title}
            </h2>
            <div className="inline-flex items-center gap-1 text-[10px] uppercase font-bold text-slate-500">
              <Filter className="w-3 h-3" />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="ml-1 border border-slate-300 rounded px-2 py-1 text-[11px] bg-white"
                data-testid="reports-filter-status"
              >
                <option value="">{t.filter_all}</option>
                {Object.keys(STATUS_STYLE).map((s) => (
                  <option key={s} value={s}>{STATUS_STYLE[s].labels[lang] || STATUS_STYLE[s].labels.no}</option>
                ))}
              </select>
            </div>
          </div>
          {allReports.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-xl p-4 text-sm text-slate-500">{t.admin_empty}</div>
          ) : (
            <ul className="space-y-2">
              {allReports.map((r) => (
                <ReportCard
                  key={r.id}
                  r={r}
                  lang={lang}
                  t={t}
                  expanded={expandedId === r.id}
                  onToggle={() => setExpandedId(expandedId === r.id ? null : r.id)}
                  onSetStatus={(s) => patchStatus(r.id, s)}
                  onReply={(text) => sendReply(r.id, text)}
                  onDelete={() => del(r.id)}
                  isAdmin
                />
              ))}
            </ul>
          )}
        </section>
      )}
    </DoserneShell>
  );
}

function ReportCard({ r, lang, t, isAdmin = false, readonly = false, expanded = false, onToggle, onSetStatus, onReply, onDelete }) {
  const cat = CATEGORIES.find((c) => c.id === r.category) || CATEGORIES[CATEGORIES.length - 1];
  const CatIcon = cat.icon;
  const status = STATUS_STYLE[r.status] || STATUS_STYLE.open;
  const StatusIcon = status.icon;
  const [replyText, setReplyText] = React.useState("");
  const authorLabel = r.is_anonymous ? t.anonymous_by : (r.created_by_name || r.created_by_email || "?");
  return (
    <li className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid={`report-${r.id}`}>
      <button
        type="button"
        onClick={onToggle}
        disabled={readonly}
        className="w-full p-3 text-left hover:bg-slate-50 disabled:hover:bg-white disabled:cursor-default"
      >
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
            <CatIcon className="w-4 h-4 text-slate-700" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wide ${status.bg} ${status.text}`}>
                <StatusIcon className="w-3 h-3" /> {status.labels[lang] || status.labels.no}
              </span>
              {r.is_anonymous && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wide bg-slate-100 text-slate-600">
                  <EyeOff className="w-3 h-3" /> {t.anonymous_by}
                </span>
              )}
            </div>
            <div className="text-sm font-black text-slate-900 truncate">{r.subject}</div>
            <div className="text-[11px] text-slate-500 flex items-center gap-2 mt-0.5">
              <span>{authorLabel}</span>
              <span>·</span>
              <span>{fmtDate(r.created_at, lang)}</span>
            </div>
          </div>
          {!readonly && (
            <div className="flex-shrink-0 text-slate-400">
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </div>
          )}
        </div>
      </button>

      {(readonly || expanded) && (
        <div className="px-3 pb-3 border-t border-slate-100 pt-2">
          <p className="text-sm text-slate-700 whitespace-pre-wrap">{r.body}</p>

          {r.admin_reply && (
            <div className="mt-3 p-3 rounded-lg bg-sky-50 border border-sky-200" data-testid={`report-reply-${r.id}`}>
              <div className="text-[10px] uppercase font-black tracking-wide text-sky-700 inline-flex items-center gap-1"><MessageSquare className="w-3 h-3" /> {t.replied_by} {r.replied_by_name}</div>
              <p className="text-sm text-slate-800 mt-1 whitespace-pre-wrap">{r.admin_reply}</p>
            </div>
          )}

          {isAdmin && !readonly && (
            <div className="mt-3 space-y-2">
              <div className="flex flex-wrap gap-2">
                {Object.keys(STATUS_STYLE).map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => onSetStatus(s)}
                    disabled={r.status === s}
                    className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wide border ${r.status === s ? `${STATUS_STYLE[s].bg} ${STATUS_STYLE[s].text} border-transparent` : "bg-white text-slate-600 border-slate-300 hover:border-slate-500"}`}
                    data-testid={`report-status-${r.id}-${s}`}
                  >
                    {STATUS_STYLE[s].labels[lang] || STATUS_STYLE[s].labels.no}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={onDelete}
                  className="ml-auto text-xs text-red-500 hover:text-red-700 inline-flex items-center gap-1"
                  data-testid={`report-delete-${r.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="flex gap-2">
                <input
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder={t.reply_ph}
                  maxLength={3000}
                  className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm"
                  data-testid={`report-reply-input-${r.id}`}
                />
                <button
                  type="button"
                  onClick={() => { onReply(replyText); setReplyText(""); }}
                  disabled={!replyText.trim()}
                  className="px-3 py-2 rounded-lg bg-slate-900 hover:bg-slate-800 disabled:bg-slate-300 text-xs font-black inline-flex items-center gap-1"
                  style={{ color: "#fff" }}
                  data-testid={`report-reply-send-${r.id}`}
                >
                  <Send className="w-3.5 h-3.5" /> {t.reply_send}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </li>
  );
}
